/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  ScrollView,
  StyleSheet,
  Switch,
  Text,
  TextInput,
  TouchableOpacity,
  View,
} from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { useBranchPermission } from '../../shell/session/hooks/useBranchPermission';
import { usePlanEntitlements } from '../../shell/providers/PlanEntitlementsProvider';
import { useEmployeesStore, withSelfOwner, type EmployeeRecord } from '../../team/store/useEmployeesStore';
import { plural } from '../../shared/format/plural';
import { formatMoney } from '../../shared/format/public-api';
import { spacing } from '../../shared/theme/spacing';
import { ticketColors, ticketFonts } from '../../shared/theme/ticketColors'
import { PaperSheet } from '../../shared/ticket/PaperSheet'
import { TicketModuleHeader } from '../../shared/ticket/TicketModuleHeader'
import { TicketListRow } from '../../shared/ticket/TicketListRow'
import { TicketButton } from '../../shared/ticket/TicketButton'
import { TicketSheet } from '../../shared/ticket/TicketSheet'
import { ticketControls } from '../../shared/ticket/ticketControls.styles'
import { OperationalScrollScreen } from '../../shared/ticket/OperationalScrollScreen';
import { ModuleEmptyState } from '../../shared/ui/ModuleEmptyState'
import { useCapabilityGate } from '../../shell/session/hooks/useCapabilityGate';
import { showSuccess, showError } from '../../shared/ui/appToast'
import { useOperationalConfirm } from '../../shared/ui/useOperationalConfirm';
import {
  listCommissionPlans,
  listCommissionRules,
  saveCommissionPlan,
  deleteCommissionPlan,
  createCommissionRule,
  updateCommissionRule,
  deleteCommissionRule,
  duplicateCommissionPlan,
  listCommissionAssignments,
  assignEmployeeToPlan,
  unassignEmployeeFromPlan,
  simulateCommission,
} from '../infra/commissionsRepo';
import { listCatalogProducts } from '../../catalog/infra/catalogReadRepo'
import { listCategories } from '../../catalog/infra/categoriesRepoRead';
import type {
  CommissionPlan,
  CommissionRule,
  CommissionAssignment,
  CommissionRuleType,
  CommissionScope,
  CommissionBase,
  AttributionRole,
  TierConfig,
} from '../domain/commissionTypes';
import {
  RULE_TYPE_LABELS,
  SCOPE_LABELS,
  COMMISSION_BASE_LABELS,
  ATTRIBUTION_ROLE_LABELS,
} from '../domain/commissionTypes';

const RULE_TYPES: CommissionRuleType[] = ['percentage', 'fixed_per_unit', 'fixed_per_line', 'tiered', 'goal'];
const SCOPES: CommissionScope[] = ['all', 'service', 'product', 'category', 'employee', 'branch'];
const BASES: CommissionBase[] = ['line_total', 'line_total_pre_discount', 'unit_price', 'quantity'];
const ROLES: AttributionRole[] = ['cashier', 'sold_by', 'performed_by', 'split'];


export function CommissionPlansScreen() {
  const currentBranchId = useSessionStore((s) => s.currentBranchId);
  const { canEdit, actions } = useBranchPermission('Commissions');
  const { entitlements } = usePlanEntitlements();
  const isOwner = entitlements?.is_owner === true;
  const canManagePlans = isOwner || actions.manage_plans === true || canEdit;
  const { confirm, ConfirmModal } = useOperationalConfirm();
  const capabilityGate = useCapabilityGate('commissions', {
    title: 'Comisiones no disponibles',
    subtitle: 'Este rubro no tiene comisiones.',
  });

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [plans, setPlans] = useState<CommissionPlan[]>([]);
  const [assignments, setAssignments] = useState<CommissionAssignment[]>([]);
  const [selectedPlanId, setSelectedPlanId] = useState<string | null>(null);
  const [rules, setRules] = useState<CommissionRule[]>([]);
  const [rulesLoading, setRulesLoading] = useState(false);

  // Modals
  const [planModal, setPlanModal] = useState<{ visible: boolean; plan?: CommissionPlan }>({ visible: false });
  const [ruleModal, setRuleModal] = useState<{ visible: boolean; planId?: string; rule?: CommissionRule }>({ visible: false });
  const [assignModal, setAssignModal] = useState<{ visible: boolean; planId?: string }>({ visible: false });
  const [duplicateModal, setDuplicateModal] = useState<{ visible: boolean; plan?: CommissionPlan }>({ visible: false });
  const [simModal, setSimModal] = useState<{ visible: boolean; plan?: CommissionPlan }>({ visible: false });
  // #774: busy guard para handlers destructivos del screen principal
  const [actionBusy, setActionBusy] = useState(false);
  // Catalog name cache for scopeRef resolution
  const [catalogNames, setCatalogNames] = useState<Record<string, string>>({});

  const load = useCallback(async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true); else setLoading(true);
    setError(null);
    try {
      const [plns, asgns] = await Promise.all([
        listCommissionPlans(currentBranchId),
        listCommissionAssignments(null),
      ]);
      setPlans(plns);
      setAssignments(asgns);
    } catch (e: any) {
      setError(e?.message ?? 'Error al cargar planes.');
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [currentBranchId]);

  useFocusEffect(
    useCallback(() => {
      void load();
    }, [load]),
  );

  const loadRules = useCallback(async (planId: string) => {
    setRulesLoading(true);
    try {
      const rls = await listCommissionRules(planId);
      setRules(rls);
      // Resolve scopeRef names from catalog
      const refsToResolve = rls
        .filter((r) => r.scopeRef && !catalogNames[r.scopeRef])
        .map((r) => r.scopeRef!)
        .filter((v, i, a) => a.indexOf(v) === i);
      if (refsToResolve.length > 0) {
        void resolveCatalogNames(refsToResolve);
      }
    } catch (e: any) {
      showError('Error', e?.message ?? 'Error al cargar reglas.');
      setRules([]);
    } finally {
      setRulesLoading(false);
    }
  }, [catalogNames]);

  const resolveCatalogNames = useCallback(async (refs: string[]) => {
    try {
      const products = await listCatalogProducts({ status: 'all', itemType: 'all', limit: 500 });
      const cats = await listCategories();
      const map: Record<string, string> = {};
      for (const p of products) {
        if (p.id && refs.includes(p.id)) map[p.id] = p.name;
        if (p.sku && refs.includes(p.sku)) map[p.sku] = p.name;
      }
      for (const c of cats) {
        if (c.id && refs.includes(c.id)) map[c.id] = c.name;
        if (c.slug && refs.includes(c.slug)) map[c.slug] = c.name;
      }
      setCatalogNames((prev) => ({ ...prev, ...map }));
    } catch {
      // silent — names will show as raw refs
    }
  }, []);

  const handleSelectPlan = useCallback((planId: string) => {
    if (selectedPlanId === planId) {
      setSelectedPlanId(null);
      setRules([]);
      return;
    }
    setSelectedPlanId(planId);
    void loadRules(planId);
  }, [selectedPlanId, loadRules]);

  const handleSavePlan = useCallback(async (name: string, description: string | null, isDefault: boolean) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      const result = await saveCommissionPlan({
        name,
        planId: planModal.plan?.id ?? null,
        description,
        branchId: currentBranchId ?? null,
        isDefault,
      });
      if (result.ok) {
        const isNewPlan = !planModal.plan;
        showSuccess(
          'Plan guardado',
          isNewPlan
            ? 'Plan creado — agregá la primera regla para que calcule comisiones.'
            : 'Plan actualizado',
        );
        setPlanModal({ visible: false });
        await load(true);
        // Plan nuevo: queda expandido para agregar reglas sin un paso extra.
        if (isNewPlan && result.planId) {
          setSelectedPlanId(result.planId);
          void loadRules(result.planId);
        }
      } else {
        showError('Error', result.error ?? 'No se pudo guardar el plan.');
      }
    } catch (e: any) {
      showError('Error', e?.message ?? 'Error al guardar plan.');
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, planModal, currentBranchId, load, loadRules]);

  const handleDeletePlan = useCallback(async (plan: CommissionPlan) => {
    if (actionBusy) return;
    const ok = await confirm({
      title: 'Eliminar plan',
      message: `¿Seguro que querés eliminar "${plan.name}"?`,
      confirmLabel: 'Eliminar',
      destructive: true,
    });
    if (!ok) return;
    setActionBusy(true);
    try {
      await deleteCommissionPlan(plan.id);
      showSuccess('Plan eliminado', '');
      if (selectedPlanId === plan.id) {
        setSelectedPlanId(null);
        setRules([]);
      }
      void load(true);
    } catch (e: any) {
      showError('Error', e?.message ?? 'Error al eliminar plan.');
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, confirm, selectedPlanId, load]);

  const handleDeleteRule = useCallback(async (rule: CommissionRule) => {
    if (actionBusy) return;
    const ok = await confirm({
      title: 'Eliminar regla',
      message: `¿Seguro que querés eliminar esta regla de ${RULE_TYPE_LABELS[rule.ruleType]}?`,
      confirmLabel: 'Eliminar',
      destructive: true,
    });
    if (!ok) return;
    setActionBusy(true);
    try {
      await deleteCommissionRule(rule.id);
      showSuccess('Regla eliminada', '');
      if (selectedPlanId) void loadRules(selectedPlanId);
    } catch (e: any) {
      showError('Error', e?.message ?? 'Error al eliminar regla.');
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, confirm, selectedPlanId, loadRules]);

  const handleUnassignEmployee = useCallback(async (planId: string, employeeName: string, employeeUserId: string) => {
    if (actionBusy) return;
    const ok = await confirm({
      title: 'Quitar empleado',
      message: `¿Seguro que querés quitar a "${employeeName}" de este plan?`,
      confirmLabel: 'Quitar',
      destructive: true,
    });
    if (!ok) return;
    setActionBusy(true);
    try {
      await unassignEmployeeFromPlan(planId, employeeUserId);
      showSuccess('Desasignado', '');
      void load(true);
    } catch (e: any) {
      showError('Error', e?.message);
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, confirm, load]);

  const handleDuplicatePlan = useCallback(async (sourcePlan: CommissionPlan, newName: string) => {
    if (actionBusy) return;
    setActionBusy(true);
    try {
      await duplicateCommissionPlan({ sourcePlanId: sourcePlan.id, newName });
      showSuccess('Plan duplicado', `"${newName}" creado`);
      setDuplicateModal({ visible: false });
      void load(true);
    } catch (e: any) {
      showError('Error', e?.message ?? 'Error al duplicar plan.');
    } finally {
      setActionBusy(false);
    }
  }, [actionBusy, load]);

  const handleToggleRuleActive = useCallback(async (rule: CommissionRule) => {
    try {
      await updateCommissionRule(rule.id, { isActive: !rule.isActive });
      if (selectedPlanId) void loadRules(selectedPlanId);
    } catch (e: any) {
      showError('Error', e?.message ?? 'Error al cambiar estado de la regla.');
    }
  }, [selectedPlanId, loadRules]);

  // ---- Loading / Error states ----
  if (!capabilityGate.allowed) {
    return <OperationalScrollScreen header={null} body={capabilityGate.blocked} />;
  }

  if (loading) {
    return (
      <OperationalScrollScreen
        header={null}
        body={
          <ModuleEmptyState loading kicker="Comisiones" />
        }
      />
    );
  }

  if (error) {
    return (
      <OperationalScrollScreen
        header={null}
        body={
          <ModuleEmptyState
            variant="nest"
            kicker="Comisiones"
            title="No se pudieron cargar los planes"
            subtitle={error}
            primaryAction={{ label: 'Reintentar', onPress: () => void load() }}
          />
        }
      />
    );
  }

  const selectedPlan = plans.find((p) => p.id === selectedPlanId);
  const planAssignments = assignments.filter((a) => a.planId === selectedPlanId);

  const header = (
    <TicketModuleHeader
      kicker="COMISIONES"
      title="Planes de comisión"
      meta={`${plans.length} ${plural(plans.length, 'plan', 'planes')} · ${assignments.length} ${plural(assignments.length, 'asignación', 'asignaciones')}`}
      // Sin planes el CTA vive en el empty state — no duplicar «+ Plan».
      trailing={
        canManagePlans && plans.length > 0 ? (
          <TicketButton label="+ Plan" variant="gold" onPress={() => setPlanModal({ visible: true })} />
        ) : null
      }
    />
  );

  const body = (
    <>
      {plans.length === 0 ? (
        <ModuleEmptyState
          variant="nest"
          kicker="Comisiones"
          title="No hay planes de comisión"
          subtitle="Creá el primer plan de comisión para empezar a configurar reglas y asignar empleados."
          primaryAction={
            canManagePlans
              ? { label: '+ Plan', onPress: () => setPlanModal({ visible: true }) }
              : undefined
          }
        />
      ) : (
        <PaperSheet>
          {plans.map((p, idx) => {
            const pAssignments = assignments.filter((a) => a.planId === p.id && a.isActive);
            const isSelected = selectedPlanId === p.id;
            const isLast = idx === plans.length - 1;
            return (
              <TicketListRow
                key={p.id}
                title={p.name}
                subtitle={p.description ?? null}
                meta={`${p.ruleCount} ${plural(p.ruleCount, 'regla', 'reglas')} · ${pAssignments.length} ${plural(pAssignments.length, 'empleado', 'empleados')}${p.branchId ? ' · Sucursal' : ' · Todas las sucursales'}`}
                statusLabel={p.isDefault ? 'Default' : null}
                statusTone="ok"
                showChevron
                onPress={() => handleSelectPlan(p.id)}
                group={isLast ? 'groupedLast' : 'grouped'}
                footer={
                  <>
                    {canManagePlans ? (
                      <View style={styles.cardActions}>
                        <TicketButton label="Editar" variant="ghost" onPress={() => setPlanModal({ visible: true, plan: p })} />
                        <TicketButton label="Asignar" variant="ghost" onPress={() => setAssignModal({ visible: true, planId: p.id })} />
                        <TicketButton label="Duplicar" variant="ghost" onPress={() => setDuplicateModal({ visible: true, plan: p })} />
                        <TicketButton label="Simular" variant="ghost" onPress={() => setSimModal({ visible: true, plan: p })} />
                        <TicketButton label="Eliminar" variant="danger" onPress={() => void handleDeletePlan(p)} />
                      </View>
                    ) : null}

                    {isSelected ? (
                      <View style={styles.rulesSection}>
                        {/* Auto-generated summary from rules */}
                        {rules.length > 0 && !rulesLoading ? (
                          <View style={styles.summaryBox}>
                            <Text style={styles.summaryText}>
                              {buildPlanSummary(rules, catalogNames)}
                            </Text>
                          </View>
                        ) : null}

                        <View style={styles.rulesHeader}>
                          <Text style={styles.rulesTitle}>Reglas ({rules.length})</Text>
                          {canManagePlans ? (
                            <TicketButton
                              label="+ Regla"
                              variant="gold"
                              onPress={() => setRuleModal({ visible: true, planId: p.id })}
                            />
                          ) : null}
                        </View>
                        {rulesLoading ? (
                          <ActivityIndicator size="small" color={ticketColors.gold} />
                        ) : rules.length === 0 ? (
                          <Text style={styles.emptySmall}>No hay reglas. Agregá la primera.</Text>
                        ) : (
                          rules.map((r) => (
                            <RuleCard
                              key={r.id}
                              rule={r}
                              canManage={canManagePlans}
                              catalogNames={catalogNames}
                              onDelete={() => void handleDeleteRule(r)}
                              onEdit={() => setRuleModal({ visible: true, planId: p.id, rule: r })}
                              onToggleActive={() => void handleToggleRuleActive(r)}
                            />
                          ))
                        )}

                        {pAssignments.length > 0 ? (
                          <View style={styles.assignmentsSection}>
                            <Text style={styles.rulesTitle}>Empleados asignados</Text>
                            {pAssignments.map((a) => (
                              <View key={a.id} style={styles.assignmentRow}>
                                <Text style={styles.assignmentName}>{a.employeeName}</Text>
                                {canManagePlans ? (
                                  <TicketButton
                                    label="Quitar"
                                    variant="danger"
                                    onPress={() => void handleUnassignEmployee(a.planId, a.employeeName, a.employeeUserId)}
                                  />
                                ) : null}
                              </View>
                            ))}
                          </View>
                        ) : null}
                      </View>
                    ) : null}
                  </>
                }
              />
            );
          })}
        </PaperSheet>
      )}
    </>
  );

  const modals = (
    <>
      {/* Plan modal */}
      <PlanModal
        visible={planModal.visible}
        plan={planModal.plan}
        onClose={() => setPlanModal({ visible: false })}
        onSave={handleSavePlan}
      />

      {/* Rule modal */}
      <RuleModal
        visible={ruleModal.visible}
        planId={ruleModal.planId ?? null}
        editingRule={ruleModal.rule}
        onClose={() => setRuleModal({ visible: false })}
        onSaved={() => {
          setRuleModal({ visible: false });
          if (selectedPlanId) void loadRules(selectedPlanId);
        }}
      />

      {/* Assign modal */}
      <AssignModal
        visible={assignModal.visible}
        planId={assignModal.planId ?? null}
        assignedUserIds={assignments
          .filter((a) => a.planId === assignModal.planId && a.isActive)
          .map((a) => a.employeeUserId)}
        onClose={() => setAssignModal({ visible: false })}
        onAssigned={() => {
          setAssignModal({ visible: false });
          void load(true);
        }}
      />

      {/* Duplicate plan modal */}
      <DuplicateModal
        visible={duplicateModal.visible}
        plan={duplicateModal.plan}
        onClose={() => setDuplicateModal({ visible: false })}
        onDuplicate={(name) => duplicateModal.plan && void handleDuplicatePlan(duplicateModal.plan, name)}
      />

      {/* Simulator modal */}
      <SimulatorModal
        visible={simModal.visible}
        plan={simModal.plan}
        assignments={assignments.filter((a) => a.planId === (simModal.plan?.id ?? '') && a.isActive)}
        branchId={currentBranchId ?? null}
        onClose={() => setSimModal({ visible: false })}
      />

      {/* Confirm modal (delete plan / delete rule / unassign employee) */}
      {ConfirmModal}
    </>
  );

  return (
    <OperationalScrollScreen
      header={header}
      body={body}
      modals={modals}
      refreshing={refreshing}
      onRefresh={() => void load(true)}
    />
  );
}

// ============================================================================
// Rule Card — chips-based visual
// ============================================================================
function RuleCard({ rule, canManage, catalogNames, onDelete, onEdit, onToggleActive }: {
  rule: CommissionRule;
  canManage: boolean;
  catalogNames: Record<string, string>;
  onDelete: () => void;
  onEdit: () => void;
  onToggleActive: () => void;
}) {
  const scopeRefLabel = rule.scopeRef
    ? (catalogNames[rule.scopeRef] ?? rule.scopeRef)
    : null;
  const scopeLabel = scopeRefLabel
    ? `${SCOPE_LABELS[rule.scope]}: ${scopeRefLabel}`
    : SCOPE_LABELS[rule.scope];
  return (
    <View style={[styles.ruleCard, !rule.isActive && styles.ruleCardInactive]}>
      <View style={styles.ruleHeader}>
        <View style={styles.ruleHeaderLeft}>
          <Text style={styles.ruleType}>{RULE_TYPE_LABELS[rule.ruleType]}</Text>
          <Text style={styles.ruleScope}>{scopeLabel}</Text>
        </View>
        {canManage ? (
          <Switch
            value={rule.isActive}
            onValueChange={onToggleActive}
            style={styles.ruleSwitch}
          />
        ) : null}
      </View>
      <View style={styles.ruleDetails}>
        {rule.ratePercent != null ? (
          <View style={styles.chip}><Text style={styles.chipText}>{rule.ratePercent}%</Text></View>
        ) : null}
        {rule.fixedAmount != null ? (
          <View style={styles.chip}><Text style={styles.chipText}>{formatMoney(rule.fixedAmount)}</Text></View>
        ) : null}
        <View style={styles.chip}><Text style={styles.chipText}>{COMMISSION_BASE_LABELS[rule.commissionBase] ?? rule.commissionBase}</Text></View>
        <View style={styles.chip}><Text style={styles.chipText}>{ATTRIBUTION_ROLE_LABELS[rule.attributionRole] ?? rule.attributionRole}</Text></View>
        {rule.priority !== 0 ? (
          <View style={styles.chip}><Text style={styles.chipText}>Prioridad {rule.priority}</Text></View>
        ) : null}
        {rule.dayOfWeek != null ? (
          <View style={styles.chip}><Text style={styles.chipText}>Día {['Dom','Lun','Mar','Mié','Jue','Vie','Sáb'][rule.dayOfWeek]}</Text></View>
        ) : null}
        {rule.tiers.length > 0 ? (
          <View style={styles.chip}><Text style={styles.chipText}>{rule.tiers.length} {plural(rule.tiers.length, 'escalón', 'escalones')}</Text></View>
        ) : null}
      </View>
      {canManage ? (
        <View style={styles.ruleActions}>
          <TicketButton label="Editar" variant="ghost" onPress={onEdit} style={styles.ruleActionBtn} />
          <TicketButton label="Eliminar" variant="danger" onPress={onDelete} style={styles.ruleActionBtn} />
        </View>
      ) : null}
    </View>
  );
}

// ============================================================================
// Plan Summary — auto-generated from rules
// ============================================================================
function buildPlanSummary(rules: CommissionRule[], catalogNames: Record<string, string>): string {
  const parts: string[] = [];
  const general = rules.filter((r) => r.scope === 'all' && r.isActive);
  const specific = rules.filter((r) => r.scope !== 'all' && r.isActive);

  for (const r of general) {
    if (r.ratePercent != null) {
      parts.push(`${r.ratePercent}% ${ATTRIBUTION_ROLE_LABELS[r.attributionRole] ?? r.attributionRole}`);
    } else if (r.fixedAmount != null) {
      parts.push(`${formatMoney(r.fixedAmount)} ${ATTRIBUTION_ROLE_LABELS[r.attributionRole] ?? r.attributionRole}`);
    }
  }
  for (const r of specific) {
    const name = r.scopeRef ? (catalogNames[r.scopeRef] ?? r.scopeRef) : SCOPE_LABELS[r.scope];
    if (r.ratePercent != null) {
      parts.push(`${r.ratePercent}% ${name}`);
    } else if (r.fixedAmount != null) {
      parts.push(`${formatMoney(r.fixedAmount)} ${name}`);
    }
  }
  if (parts.length === 0) return 'Sin reglas activas';
  return parts.join(' · ');
}

// ============================================================================
// Plan Modal
// ============================================================================
function PlanModal({ visible, plan, onClose, onSave }: {
  visible: boolean;
  plan?: CommissionPlan;
  onClose: () => void;
  onSave: (name: string, description: string | null, isDefault: boolean) => void;
}) {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);

  React.useEffect(() => {
    if (visible) {
      setName(plan?.name ?? '');
      setDescription(plan?.description ?? '');
      setIsDefault(plan?.isDefault ?? false);
    }
  }, [visible, plan]);

  return (
    <TicketSheet
      visible={visible}
      title={plan ? 'Editar plan' : 'Nuevo plan'}
      onClose={onClose}
      fitContent
      dialogStyle={{ maxWidth: 440, width: '90%' }}
      footer={
        <View style={styles.modalActions}>
          <TicketButton label="Cancelar" variant="ghost" onPress={onClose} style={{ flex: 1 }} />
          <TicketButton
            label="Guardar"
            variant="gold"
            onPress={() => {
              if (!name.trim()) { showError('Error', 'Ingresá un nombre.'); return; }
              onSave(name.trim(), description.trim() || null, isDefault);
            }}
            style={{ flex: 1 }}
          />
        </View>
      }
    >
      <View style={styles.modalBody}>
        <Text style={ticketControls.fieldLabel}>Nombre</Text>
        <TextInput
          style={ticketControls.input}
          value={name}
          onChangeText={setName}
          placeholder="Plan básico 10%"
          placeholderTextColor={ticketColors.ink3}
        />

        <Text style={ticketControls.fieldLabel}>
          Descripción <Text style={styles.fieldHint}>(opcional)</Text>
        </Text>
        <TextInput
          style={[ticketControls.input, styles.inputMultiline]}
          value={description}
          onChangeText={setDescription}
          placeholder="Ej: 10% en servicios, 5% en productos"
          placeholderTextColor={ticketColors.ink3}
          multiline
        />
        <Text style={styles.fieldHintBlock}>
          Las comisiones se calculan con las reglas del plan — esto es solo una
          nota para vos.
        </Text>

        <View style={styles.switchRow}>
          <View style={styles.switchLabelWrap}>
            <Text style={ticketControls.fieldLabel}>Plan principal</Text>
            <Text style={styles.fieldHintBlock}>
              Es el que se sugiere al asignar empleados. Solo puede haber uno.
            </Text>
          </View>
          <Switch value={isDefault} onValueChange={setIsDefault} />
        </View>
        {!plan ? (
          <Text style={styles.fieldHintBlock}>
            Después de guardar vas a poder agregar las reglas que calculan la
            comisión.
          </Text>
        ) : null}
      </View>
    </TicketSheet>
  );
}

// ============================================================================
// Rule Modal — create + edit + scopeRef picker + tiers
// ============================================================================
type PickerOption = { id: string; name: string };

function RuleModal({ visible, planId, editingRule, onClose, onSaved }: {
  visible: boolean;
  planId: string | null;
  editingRule?: CommissionRule;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [ruleType, setRuleType] = useState<CommissionRuleType>('percentage');
  const [scope, setScope] = useState<CommissionScope>('all');
  const [scopeRef, setScopeRef] = useState('');
  const [ratePercent, setRatePercent] = useState('');
  const [fixedAmount, setFixedAmount] = useState('');
  const [commissionBase, setCommissionBase] = useState<CommissionBase>('line_total');
  const [attributionRole, setAttributionRole] = useState<AttributionRole>('performed_by');
  const [priority, setPriority] = useState('0');
  const [tiers, setTiers] = useState<TierConfig[]>([]);
  const [saving, setSaving] = useState(false);
  const [pickerOptions, setPickerOptions] = useState<PickerOption[]>([]);
  const [pickerLoading, setPickerLoading] = useState(false);
  const [pickerSearch, setPickerSearch] = useState('');

  const isEdit = Boolean(editingRule);
  const needsScopeRef = scope !== 'all';
  const needsRate = ruleType === 'percentage' || ruleType === 'tiered' || ruleType === 'goal';
  const needsFixed = ruleType === 'fixed_per_unit' || ruleType === 'fixed_per_line';
  const needsTiers = ruleType === 'tiered';

  // Load editing rule or reset
  useEffect(() => {
    if (!visible) return;
    if (editingRule) {
      setRuleType(editingRule.ruleType);
      setScope(editingRule.scope);
      setScopeRef(editingRule.scopeRef ?? '');
      setRatePercent(editingRule.ratePercent != null ? String(editingRule.ratePercent) : '');
      setFixedAmount(editingRule.fixedAmount != null ? String(editingRule.fixedAmount) : '');
      setCommissionBase(editingRule.commissionBase);
      setAttributionRole(editingRule.attributionRole);
      setPriority(String(editingRule.priority));
      setTiers(editingRule.tiers ?? []);
    } else {
      setRuleType('percentage'); setScope('all'); setScopeRef('');
      setRatePercent(''); setFixedAmount(''); setCommissionBase('line_total');
      setAttributionRole('performed_by'); setPriority('0'); setTiers([]);
    }
    setPickerSearch('');
  }, [visible, editingRule]);

  // Load picker options when scope needs a ref
  useEffect(() => {
    if (!visible || !needsScopeRef) { setPickerOptions([]); return; }
    setPickerLoading(true);
    setPickerSearch('');
    void (async () => {
      try {
        if (scope === 'service' || scope === 'product') {
          const products = await listCatalogProducts({
            status: 'active',
            itemType: scope === 'service' ? 'service' : 'all',
            limit: 200,
          });
          setPickerOptions(products.map((p) => ({ id: p.id, name: p.name })));
        } else if (scope === 'category') {
          const cats = await listCategories();
          setPickerOptions(cats.map((c) => ({ id: c.id, name: c.name })));
        } else {
          setPickerOptions([]);
        }
      } catch {
        setPickerOptions([]);
      } finally {
        setPickerLoading(false);
      }
    })();
  }, [visible, scope, needsScopeRef]);

  const filteredPickerOptions = useMemo(() => {
    if (!pickerSearch.trim()) return pickerOptions;
    const q = pickerSearch.trim().toLowerCase();
    return pickerOptions.filter((o) => o.name.toLowerCase().includes(q));
  }, [pickerOptions, pickerSearch]);

  const handleSave = useCallback(async () => {
    if (!planId) return;
    if (needsScopeRef && !scopeRef.trim()) {
      showError('Error', 'Seleccioná el elemento del scope.');
      return;
    }
    if (needsRate && !ratePercent.trim()) {
      showError('Error', 'Ingresá el porcentaje.');
      return;
    }
    if (needsFixed && !fixedAmount.trim()) {
      showError('Error', 'Ingresá el monto fijo.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ruleType,
        scope,
        scopeRef: needsScopeRef ? scopeRef.trim() : null,
        ratePercent: needsRate ? parseFloat(ratePercent) || null : null,
        fixedAmount: needsFixed ? parseFloat(fixedAmount) || null : null,
        commissionBase,
        attributionRole,
        priority: parseInt(priority) || 0,
        tiers: needsTiers ? tiers : [],
      };
      if (isEdit && editingRule) {
        await updateCommissionRule(editingRule.id, payload);
        showSuccess('Regla actualizada', '');
      } else {
        await createCommissionRule({ planId, ...payload });
        showSuccess('Regla creada', '');
      }
      onSaved();
    } catch (e: any) {
      showError('Error', e?.message ?? 'Error al guardar regla.');
    } finally {
      setSaving(false);
    }
  }, [planId, isEdit, editingRule, ruleType, scope, scopeRef, ratePercent, fixedAmount, commissionBase, attributionRole, priority, tiers, needsScopeRef, needsRate, needsFixed, needsTiers, onSaved]);

  const addTier = useCallback(() => {
    setTiers((prev) => [...prev, { min_amount: 0, rate_percent: 0 }]);
  }, []);

  const updateTier = useCallback((idx: number, field: 'min_amount' | 'min_qty' | 'rate_percent', value: number) => {
    setTiers((prev) => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  }, []);

  const removeTier = useCallback((idx: number) => {
    setTiers((prev) => prev.filter((_, i) => i !== idx));
  }, []);

  return (
    <RuleModalInner
      visible={visible}
      isEdit={isEdit}
      ruleType={ruleType}
      setRuleType={setRuleType}
      scope={scope}
      setScope={setScope}
      scopeRef={scopeRef}
      setScopeRef={setScopeRef}
      ratePercent={ratePercent}
      setRatePercent={setRatePercent}
      fixedAmount={fixedAmount}
      setFixedAmount={setFixedAmount}
      commissionBase={commissionBase}
      setCommissionBase={setCommissionBase}
      attributionRole={attributionRole}
      setAttributionRole={setAttributionRole}
      priority={priority}
      setPriority={setPriority}
      tiers={tiers}
      addTier={addTier}
      updateTier={updateTier}
      removeTier={removeTier}
      needsScopeRef={needsScopeRef}
      needsRate={needsRate}
      needsFixed={needsFixed}
      needsTiers={needsTiers}
      pickerLoading={pickerLoading}
      pickerOptions={pickerOptions}
      filteredPickerOptions={filteredPickerOptions}
      pickerSearch={pickerSearch}
      setPickerSearch={setPickerSearch}
      saving={saving}
      onClose={onClose}
      onSave={() => void handleSave()}
    />
  );
}

// ============================================================================
// Rule Modal Inner — sectioned Square-like layout
// ============================================================================
function RuleModalInner(props: {
  visible: boolean;
  isEdit: boolean;
  ruleType: CommissionRuleType;
  setRuleType: (t: CommissionRuleType) => void;
  scope: CommissionScope;
  setScope: (s: CommissionScope) => void;
  scopeRef: string;
  setScopeRef: (s: string) => void;
  ratePercent: string;
  setRatePercent: (s: string) => void;
  fixedAmount: string;
  setFixedAmount: (s: string) => void;
  commissionBase: CommissionBase;
  setCommissionBase: (b: CommissionBase) => void;
  attributionRole: AttributionRole;
  setAttributionRole: (r: AttributionRole) => void;
  priority: string;
  setPriority: (s: string) => void;
  tiers: TierConfig[];
  addTier: () => void;
  updateTier: (idx: number, field: 'min_amount' | 'min_qty' | 'rate_percent', value: number) => void;
  removeTier: (idx: number) => void;
  needsScopeRef: boolean;
  needsRate: boolean;
  needsFixed: boolean;
  needsTiers: boolean;
  pickerLoading: boolean;
  pickerOptions: PickerOption[];
  filteredPickerOptions: PickerOption[];
  pickerSearch: string;
  setPickerSearch: (s: string) => void;
  saving: boolean;
  onClose: () => void;
  onSave: () => void;
}) {
  const [showAdvanced, setShowAdvanced] = useState(false);
  const {
    visible, isEdit, ruleType, setRuleType, scope, setScope, scopeRef, setScopeRef,
    ratePercent, setRatePercent, fixedAmount, setFixedAmount,
    commissionBase, setCommissionBase, attributionRole, setAttributionRole,
    priority, setPriority, tiers, addTier, updateTier, removeTier,
    needsScopeRef, needsRate, needsFixed, needsTiers,
    pickerLoading, filteredPickerOptions, pickerSearch, setPickerSearch,
    saving, onClose, onSave,
  } = props;

  const scopeRefLabel = scope === 'service' ? 'Servicio' : scope === 'product' ? 'Producto' : scope === 'category' ? 'Categoría' : 'Referencia';

  return (
    <TicketSheet
      visible={visible}
      title={isEdit ? 'Editar regla' : 'Nueva regla'}
      onClose={onClose}
      dialogStyle={{ maxWidth: 560, width: '90%', maxHeight: '90%' }}
      footer={
        <View style={styles.modalActions}>
          <TicketButton label="Cancelar" variant="ghost" onPress={onClose} style={{ flex: 1 }} />
          <TicketButton
            label={saving ? 'Guardando…' : 'Guardar'}
            variant="gold"
            onPress={onSave}
            disabled={saving}
            style={{ flex: 1 }}
          />
        </View>
      }
    >
      <ScrollView
        style={styles.modalBodyScroll}
        contentContainerStyle={styles.modalBodyContent}
        showsVerticalScrollIndicator={false}
      >
        {/* ===== Section 1: Tipo de comisión ===== */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Tipo de comisión</Text>
        </View>
        <View style={styles.typeRow}>
          {RULE_TYPES.map((t) => (
            <TouchableOpacity
              key={t}
              style={[styles.typeBtn, ruleType === t && styles.typeBtnActive]}
              onPress={() => setRuleType(t)}
            >
              <Text style={[styles.typeBtnText, ruleType === t && styles.typeBtnTextActive]}>
                {RULE_TYPE_LABELS[t]}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {/* ===== Section 2: Valor ===== */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Valor de la comisión</Text>
        </View>
        {needsRate ? (
          <View style={styles.fieldGroup}>
            <Text style={ticketControls.fieldLabel}>Porcentaje (%)</Text>
            <TextInput
              style={ticketControls.input}
              value={ratePercent}
              onChangeText={setRatePercent}
              placeholder="10"
              placeholderTextColor={ticketColors.ink3}
              keyboardType="decimal-pad"
            />
          </View>
        ) : null}
        {needsFixed ? (
          <View style={styles.fieldGroup}>
            <Text style={ticketControls.fieldLabel}>Monto fijo</Text>
            <TextInput
              style={ticketControls.input}
              value={fixedAmount}
              onChangeText={setFixedAmount}
              placeholder="0.00"
              placeholderTextColor={ticketColors.ink3}
              keyboardType="decimal-pad"
            />
          </View>
        ) : null}
        {needsTiers ? (
          <View style={styles.fieldGroup}>
            <View style={styles.tiersHeader}>
              <Text style={ticketControls.fieldLabel}>Escalones</Text>
              <TicketButton label="+ Escalón" variant="ghost" onPress={addTier} />
            </View>
            {tiers.length === 0 ? (
              <Text style={styles.emptySmall}>Sin escalones. Agregá al menos uno.</Text>
            ) : (
              tiers.map((tier, idx) => (
                <View key={idx} style={styles.tierRow}>
                  <View style={styles.tierField}>
                    <Text style={styles.tierFieldLabel}>Desde $</Text>
                    <TextInput
                      style={[ticketControls.input, styles.tierInput]}
                      value={String(tier.min_amount ?? 0)}
                      onChangeText={(v) => updateTier(idx, 'min_amount', parseFloat(v) || 0)}
                      placeholder="0"
                      placeholderTextColor={ticketColors.ink3}
                      keyboardType="decimal-pad"
                    />
                  </View>
                  <View style={styles.tierField}>
                    <Text style={styles.tierFieldLabel}>%</Text>
                    <TextInput
                      style={[ticketControls.input, styles.tierInput]}
                      value={String(tier.rate_percent)}
                      onChangeText={(v) => updateTier(idx, 'rate_percent', parseFloat(v) || 0)}
                      placeholder="0"
                      placeholderTextColor={ticketColors.ink3}
                      keyboardType="decimal-pad"
                    />
                  </View>
                  <TouchableOpacity onPress={() => removeTier(idx)} style={styles.tierRemove} hitSlop={8}>
                    <Ionicons name="trash-outline" size={18} color={ticketColors.bad} />
                  </TouchableOpacity>
                </View>
              ))
            )}
          </View>
        ) : null}

        {/* ===== Section 3: Alcance ===== */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Alcance</Text>
          <Text style={styles.sectionHint}>¿A qué ventas aplica?</Text>
        </View>
        <View style={styles.typeRow}>
          {SCOPES.map((s) => (
            <TouchableOpacity
              key={s}
              style={[styles.typeBtn, scope === s && styles.typeBtnActive]}
              onPress={() => setScope(s)}
            >
              <Text style={[styles.typeBtnText, scope === s && styles.typeBtnTextActive]}>
                {SCOPE_LABELS[s]}
              </Text>
            </TouchableOpacity>
          ))}
        </View>
        {needsScopeRef ? (
          <View style={styles.fieldGroup}>
            <Text style={ticketControls.fieldLabel}>{scopeRefLabel}</Text>
            {pickerLoading ? (
              <ActivityIndicator size="small" color={ticketColors.gold} style={{ paddingVertical: spacing.sm }} />
            ) : filteredPickerOptions.length > 0 || pickerSearch ? (
              <>
                <View style={styles.searchWrap}>
                  <Ionicons name="search-outline" size={16} color={ticketColors.ink3} style={styles.searchIcon} />
                  <TextInput
                    style={styles.searchInput}
                    value={pickerSearch}
                    onChangeText={setPickerSearch}
                    placeholder="Buscar…"
                    placeholderTextColor={ticketColors.ink3}
                  />
                </View>
                <View style={styles.pickerList}>
                  {filteredPickerOptions.slice(0, 50).map((opt) => (
                    <TouchableOpacity
                      key={opt.id}
                      style={[styles.pickerRow, scopeRef === opt.id && styles.pickerRowActive]}
                      onPress={() => setScopeRef(opt.id)}
                    >
                      <Text style={[styles.pickerRowText, scopeRef === opt.id && styles.pickerRowTextActive]}>
                        {opt.name}
                      </Text>
                      {scopeRef === opt.id ? (
                        <Ionicons name="checkmark-circle" size={18} color={ticketColors.gold} />
                      ) : null}
                    </TouchableOpacity>
                  ))}
                  {filteredPickerOptions.length === 0 && pickerSearch ? (
                    <Text style={styles.emptySmall}>Sin resultados.</Text>
                  ) : null}
                </View>
              </>
            ) : (
              <TextInput
                style={ticketControls.input}
                value={scopeRef}
                onChangeText={setScopeRef}
                placeholder="ID"
                placeholderTextColor={ticketColors.ink3}
              />
            )}
          </View>
        ) : null}

        {/* ===== Section 4: Atribución (always visible) ===== */}
        <View style={styles.sectionHeader}>
          <Text style={styles.sectionTitle}>Atribución</Text>
          <Text style={styles.sectionHint}>¿Quién recibe la comisión?</Text>
        </View>
        <View style={styles.fieldGroup}>
          <Text style={ticketControls.fieldLabel}>Rol</Text>
          <View style={styles.typeRow}>
            {ROLES.map((r) => (
              <TouchableOpacity
                key={r}
                style={[styles.typeBtn, attributionRole === r && styles.typeBtnActive]}
                onPress={() => setAttributionRole(r)}
              >
                <Text style={[styles.typeBtnText, attributionRole === r && styles.typeBtnTextActive]}>
                  {ATTRIBUTION_ROLE_LABELS[r] ?? r}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>

        {/* ===== Section 5: Avanzado (collapsible) ===== */}
        <TouchableOpacity
          style={styles.sectionHeaderClickable}
          onPress={() => setShowAdvanced((v) => !v)}
        >
          <Text style={styles.sectionTitle}>Avanzado</Text>
          <Ionicons
            name={showAdvanced ? 'chevron-up' : 'chevron-down'}
            size={16}
            color={ticketColors.ink3}
          />
        </TouchableOpacity>
        {showAdvanced ? (
          <View style={styles.advancedSection}>
            <View style={styles.fieldGroup}>
              <Text style={ticketControls.fieldLabel}>
                Base de cálculo{' '}
                <Text style={styles.fieldHint}>(sobre qué monto se calcula)</Text>
              </Text>
              <View style={styles.typeRow}>
                {BASES.map((b) => (
                  <TouchableOpacity
                    key={b}
                    style={[styles.typeBtn, commissionBase === b && styles.typeBtnActive]}
                    onPress={() => setCommissionBase(b)}
                  >
                    <Text style={[styles.typeBtnText, commissionBase === b && styles.typeBtnTextActive]}>
                      {COMMISSION_BASE_LABELS[b] ?? b}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
            <View style={styles.fieldGroup}>
              <Text style={ticketControls.fieldLabel}>
                Prioridad{' '}
                <Text style={styles.fieldHint}>(mayor = gana sobre reglas generales)</Text>
              </Text>
              <TextInput
                style={ticketControls.input}
                value={priority}
                onChangeText={setPriority}
                placeholder="0"
                placeholderTextColor={ticketColors.ink3}
                keyboardType="number-pad"
              />
            </View>
          </View>
        ) : null}
      </ScrollView>
    </TicketSheet>
  );
}

// ============================================================================
// Assign Modal
// ============================================================================
function AssignModal({ visible, planId, assignedUserIds, onClose, onAssigned }: {
  visible: boolean;
  planId: string | null;
  assignedUserIds: string[];
  onClose: () => void;
  onAssigned: () => void;
}) {
  const employeesRaw = useEmployeesStore((s) => s.employees);
  const selfOwner = useEmployeesStore((s) => s.selfOwner);
  const employees = useMemo(() => withSelfOwner(employeesRaw, selfOwner), [employeesRaw, selfOwner]);
  const loadEmployees = useEmployeesStore((s) => s.loadEmployees);
  const [search, setSearch] = useState('');
  const [saving, setSaving] = useState(false);
  const [loadingEmps, setLoadingEmps] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setSearch('');
    setLoadingEmps(true);
    void loadEmployees().finally(() => setLoadingEmps(false));
  }, [visible, loadEmployees]);

  const activeEmployees = useMemo(
    () => employees.filter((e) => e.isActive !== false && e.authUserId),
    [employees],
  );

  const filteredEmployees = useMemo(() => {
    if (!search.trim()) return activeEmployees;
    const q = search.trim().toLowerCase();
    return activeEmployees.filter(
      (e) =>
        e.name.toLowerCase().includes(q) ||
        (e.email ?? '').toLowerCase().includes(q),
    );
  }, [activeEmployees, search]);

  const handleAssign = useCallback(async (emp: EmployeeRecord) => {
    if (!planId || !emp.authUserId) return;
    setSaving(true);
    try {
      await assignEmployeeToPlan({
        planId,
        employeeUserId: emp.authUserId,
        organizationId: '',
      });
      showSuccess('Asignado', `${emp.name} asignado al plan`);
      onAssigned();
    } catch (e: any) {
      showError('Error', e?.message ?? 'Error al asignar.');
    } finally {
      setSaving(false);
    }
  }, [planId, onAssigned]);

  return (
    <TicketSheet
      visible={visible}
      title="Asignar empleado"
      subtitle="Seleccioná el empleado para asignarlo al plan:"
      onClose={onClose}
      dialogStyle={{ maxWidth: 480, width: '90%', maxHeight: '88%' }}
      footer={
        <View style={styles.modalActions}>
          <TicketButton label="Cerrar" variant="ghost" onPress={onClose} style={{ flex: 1 }} />
        </View>
      }
    >
      <View style={styles.assignBody}>
        <View style={styles.searchWrap}>
          <Ionicons name="search-outline" size={16} color={ticketColors.ink3} style={styles.searchIcon} />
          <TextInput
            style={styles.searchInput}
            value={search}
            onChangeText={setSearch}
            placeholder="Buscar empleado…"
            placeholderTextColor={ticketColors.ink3}
          />
          {search.length > 0 ? (
            <TouchableOpacity onPress={() => setSearch('')} style={styles.searchClear} hitSlop={8}>
              <Ionicons name="close-circle" size={16} color={ticketColors.ink3} />
            </TouchableOpacity>
          ) : null}
        </View>

        {loadingEmps ? (
          <ActivityIndicator size="small" color={ticketColors.gold} style={{ paddingVertical: spacing.md }} />
        ) : filteredEmployees.length === 0 ? (
          <Text style={styles.emptySmall}>
            {activeEmployees.length === 0 ? 'No hay empleados activos.' : 'Sin resultados.'}
          </Text>
        ) : (
          <ScrollView
            style={styles.assignList}
            contentContainerStyle={styles.assignListContent}
            keyboardShouldPersistTaps="handled"
            nestedScrollEnabled
          >
            {filteredEmployees.map((emp, idx) => {
              const alreadyAssigned = emp.authUserId
                ? assignedUserIds.includes(emp.authUserId)
                : false;
              return (
                <TouchableOpacity
                  key={emp.id}
                  style={[
                    styles.assignRow,
                    idx === filteredEmployees.length - 1 && styles.assignRowLast,
                    alreadyAssigned && styles.assignRowDisabled,
                  ]}
                  onPress={() => alreadyAssigned ? null : void handleAssign(emp)}
                  disabled={saving || alreadyAssigned}
                >
                  <View style={styles.assignRowInfo}>
                    <Text style={styles.assignRowName}>{emp.name}</Text>
                    <Text style={styles.assignRowMeta}>
                      {emp.email ?? 'Sin email'} · {emp.role}
                      {alreadyAssigned ? ' · Ya asignado' : ''}
                    </Text>
                  </View>
                  <View style={styles.assignRowAction}>
                    {alreadyAssigned ? (
                      <Ionicons name="checkmark-circle" size={22} color={ticketColors.ink3} />
                    ) : saving ? (
                      <ActivityIndicator size="small" color={ticketColors.gold} />
                    ) : (
                      <Ionicons name="add-circle" size={22} color={ticketColors.gold} />
                    )}
                  </View>
                </TouchableOpacity>
              );
            })}
          </ScrollView>
        )}
      </View>
    </TicketSheet>
  );
}

// ============================================================================
// Duplicate Plan Modal
// ============================================================================
function DuplicateModal({ visible, plan, onClose, onDuplicate }: {
  visible: boolean;
  plan?: CommissionPlan;
  onClose: () => void;
  onDuplicate: (newName: string) => void;
}) {
  const [name, setName] = useState('');

  useEffect(() => {
    if (visible && plan) {
      setName(`${plan.name} (copia)`);
    }
  }, [visible, plan]);

  return (
    <TicketSheet
      visible={visible}
      title="Duplicar plan"
      subtitle={plan ? `Se copiará "${plan.name}" con todas sus reglas.` : ''}
      onClose={onClose}
      fitContent
      dialogStyle={{ maxWidth: 440, width: '90%' }}
      footer={
        <View style={styles.modalActions}>
          <TicketButton label="Cancelar" variant="ghost" onPress={onClose} style={{ flex: 1 }} />
          <TicketButton
            label="Duplicar"
            variant="gold"
            onPress={() => {
              if (!name.trim()) { showError('Error', 'Ingresá un nombre.'); return; }
              onDuplicate(name.trim());
            }}
            style={{ flex: 1 }}
          />
        </View>
      }
    >
      <View style={styles.modalBody}>
        <Text style={ticketControls.fieldLabel}>Nombre del nuevo plan</Text>
        <TextInput
          style={ticketControls.input}
          value={name}
          onChangeText={setName}
          placeholder="Plan copia"
          placeholderTextColor={ticketColors.ink3}
        />
      </View>
    </TicketSheet>
  );
}

// ============================================================================
// Simulator Modal
// ============================================================================
function SimulatorModal({ visible, plan, assignments, branchId, onClose }: {
  visible: boolean;
  plan?: CommissionPlan;
  assignments: CommissionAssignment[];
  branchId: string | null;
  onClose: () => void;
}) {
  const [amount, setAmount] = useState('');
  const [selectedEmpId, setSelectedEmpId] = useState<string | null>(null);
  const [result, setResult] = useState<{ commission: number; ratePercent: number; ruleType: string } | null>(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (visible) {
      setAmount(''); setSelectedEmpId(null); setResult(null);
    }
  }, [visible]);

  const handleSimulate = useCallback(async () => {
    const amt = parseFloat(amount);
    if (!amt || amt <= 0) { showError('Error', 'Ingresá un monto válido.'); return; }
    if (!selectedEmpId) { showError('Error', 'Seleccioná un empleado.'); return; }
    setLoading(true);
    try {
      const r = await simulateCommission({
        employeeUserId: selectedEmpId,
        amount: amt,
        branchId,
      });
      if (r.ok) {
        setResult({
          commission: r.commission ?? 0,
          ratePercent: r.ratePercent ?? 0,
          ruleType: r.ruleType ?? '',
        });
      } else {
        showError('Sin resultado', r.error ?? 'No se pudo simular.');
      }
    } catch (e: any) {
      showError('Error', e?.message ?? 'Error al simular.');
    } finally {
      setLoading(false);
    }
  }, [amount, selectedEmpId, branchId]);

  return (
    <TicketSheet
      visible={visible}
      title="Simular comisión"
      subtitle={plan ? `Plan: ${plan.name}` : ''}
      onClose={onClose}
      fitContent
      dialogStyle={{ maxWidth: 440, width: '90%' }}
      footer={
        <View style={styles.modalActions}>
          <TicketButton label="Cerrar" variant="ghost" onPress={onClose} style={{ flex: 1 }} />
          <TicketButton
            label={loading ? 'Calculando…' : 'Simular'}
            variant="gold"
            onPress={() => void handleSimulate()}
            disabled={loading}
            style={{ flex: 1 }}
          />
        </View>
      }
    >
      <View style={styles.modalBody}>
        <Text style={ticketControls.fieldLabel}>Empleado</Text>
        {assignments.length === 0 ? (
          <Text style={styles.emptySmall}>No hay empleados asignados a este plan.</Text>
        ) : (
          <View style={styles.typeRow}>
            {assignments.map((a) => (
              <TouchableOpacity
                key={a.id}
                style={[styles.typeBtn, selectedEmpId === a.employeeUserId && styles.typeBtnActive]}
                onPress={() => setSelectedEmpId(a.employeeUserId)}
              >
                <Text style={[styles.typeBtnText, selectedEmpId === a.employeeUserId && styles.typeBtnTextActive]}>
                  {a.employeeName}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        )}

        <Text style={ticketControls.fieldLabel}>Monto de la venta</Text>
        <TextInput
          style={ticketControls.input}
          value={amount}
          onChangeText={setAmount}
          placeholder="20000"
          placeholderTextColor={ticketColors.ink3}
          keyboardType="decimal-pad"
        />

        {result ? (
          <View style={styles.simResult}>
            <Text style={styles.simResultLabel}>Comisión calculada</Text>
            <Text style={styles.simResultAmount}>{formatMoney(result.commission)}</Text>
            <Text style={styles.simResultMeta}>
              {result.ratePercent}% · {result.ruleType}
            </Text>
          </View>
        ) : null}
      </View>
    </TicketSheet>
  );
}

const styles = StyleSheet.create({
  cardActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    paddingTop: spacing.sm,
    flexWrap: 'wrap',
  },
  rulesSection: {
    marginTop: spacing.md,
    paddingTop: spacing.md,
    borderTopWidth: StyleSheet.hairlineWidth,
    borderTopColor: ticketColors.ruleSoft,
  },
  rulesHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.sm,
    gap: spacing.sm,
    flexWrap: 'wrap',
  },
  rulesTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  emptySmall: {
    textAlign: 'center',
    color: ticketColors.ink3,
    fontSize: 13,
    paddingVertical: spacing.md,
    fontFamily: ticketFonts.sans,
  },
  ruleCard: {
    backgroundColor: ticketColors.tile,
    borderRadius: 10,
    padding: spacing.md,
    marginBottom: spacing.sm,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  ruleCardInactive: {
    opacity: 0.5,
  },
  ruleHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  ruleHeaderLeft: {
    flex: 1,
    minWidth: 0,
  },
  ruleSwitch: {
    transform: [{ scale: 0.8 }],
  },
  ruleType: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  ruleScope: {
    fontSize: 12,
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
    marginTop: 2,
  },
  ruleDetails: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: spacing.xs,
    marginBottom: spacing.xs,
  },
  chip: {
    backgroundColor: ticketColors.paper,
    paddingHorizontal: spacing.sm,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  chipText: {
    fontSize: 11,
    fontWeight: '500',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  ruleActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    marginTop: spacing.xs,
  },
  ruleActionBtn: {
    minHeight: 30,
    paddingHorizontal: spacing.md,
  },
  // Plan summary
  summaryBox: {
    backgroundColor: ticketColors.brandSoft,
    borderRadius: 8,
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.sm,
    marginBottom: spacing.md,
  },
  summaryText: {
    fontSize: 12,
    fontWeight: '600',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  // Picker
  pickerList: {
    maxHeight: 200,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 8,
    backgroundColor: ticketColors.paper,
  },
  pickerRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.md,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  pickerRowActive: {
    backgroundColor: ticketColors.brandSoft,
  },
  pickerRowText: {
    fontSize: 13,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    flex: 1,
  },
  pickerRowTextActive: {
    fontWeight: '600',
    color: ticketColors.brandInk,
  },
  // Tiers
  tiersHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tierRow: {
    flexDirection: 'row',
    gap: spacing.sm,
    alignItems: 'center',
    marginBottom: spacing.xs,
  },
  tierInput: {
    flex: 1,
  },
  tierRemove: {
    padding: spacing.xs,
  },
  // Assign
  assignRowDisabled: {
    opacity: 0.5,
  },
  // Simulator
  simResult: {
    marginTop: spacing.md,
    backgroundColor: ticketColors.brandSoft,
    borderRadius: 10,
    padding: spacing.md,
    alignItems: 'center',
  },
  simResultLabel: {
    fontSize: 12,
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  simResultAmount: {
    fontSize: 24,
    fontWeight: '700',
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
    marginVertical: spacing.xs,
  },
  simResultMeta: {
    fontSize: 11,
    color: ticketColors.brandInk,
    fontFamily: ticketFonts.sans,
  },
  assignmentsSection: {
    marginTop: spacing.md,
  },
  assignmentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: spacing.sm,
    gap: spacing.sm,
  },
  assignmentName: {
    fontSize: 13,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    flex: 1,
  },
  // Modal
  modalBody: {
    padding: spacing.md,
  },
  fieldHint: {
    fontSize: 11,
    fontWeight: '400',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  fieldHintBlock: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    marginTop: spacing.xs,
  },
  switchLabelWrap: {
    flex: 1,
    paddingRight: spacing.sm,
  },
  modalBodyScroll: {
    flex: 1,
  },
  modalBodyContent: {
    padding: spacing.md,
    paddingBottom: spacing.lg,
  },
  // Section headers (Square-like grouping)
  sectionHeader: {
    flexDirection: 'row',
    alignItems: 'baseline',
    justifyContent: 'space-between',
    marginTop: spacing.md,
    marginBottom: spacing.sm,
    paddingBottom: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  sectionHeaderClickable: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: spacing.md,
    marginBottom: spacing.sm,
    paddingBottom: spacing.xs,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
  },
  sectionTitle: {
    fontSize: 13,
    fontWeight: '700',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    textTransform: 'uppercase',
    letterSpacing: 0.5,
  },
  sectionHint: {
    fontSize: 11,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
  },
  fieldGroup: {
    marginBottom: spacing.sm,
  },
  advancedSection: {
    marginTop: spacing.xs,
  },
  // Tier fields with labels
  tierField: {
    flex: 1,
  },
  tierFieldLabel: {
    fontSize: 10,
    fontWeight: '600',
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    marginBottom: 2,
    marginLeft: 2,
  },
  modalActions: {
    flexDirection: 'row',
    gap: spacing.sm,
    padding: spacing.md,
    paddingTop: 0,
  },
  inputMultiline: {
    height: 'auto',
    minHeight: 60,
    paddingVertical: spacing.sm,
  },
  switchRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: spacing.md,
  },
  // Assign modal
  assignBody: {
    padding: spacing.md,
    flex: 1,
  },
  searchWrap: {
    flexDirection: 'row',
    alignItems: 'center',
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
    borderRadius: 10,
    backgroundColor: ticketColors.paper,
    marginBottom: spacing.sm,
    minHeight: 38,
  },
  searchIcon: {
    paddingLeft: 10,
  },
  searchInput: {
    flex: 1,
    borderWidth: 0,
    backgroundColor: 'transparent',
    fontSize: 14,
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
    paddingHorizontal: 8,
    paddingVertical: 8,
  },
  searchClear: {
    paddingHorizontal: 10,
  },
  assignList: {
    flex: 1,
  },
  assignListContent: {
    paddingBottom: spacing.sm,
  },
  assignRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingVertical: spacing.sm,
    paddingHorizontal: spacing.sm,
    borderBottomWidth: StyleSheet.hairlineWidth,
    borderBottomColor: ticketColors.ruleSoft,
    gap: spacing.sm,
  },
  assignRowLast: {
    borderBottomWidth: 0,
  },
  assignRowInfo: {
    flex: 1,
    minWidth: 0,
  },
  assignRowName: {
    fontSize: 14,
    fontWeight: '600',
    color: ticketColors.ink,
    fontFamily: ticketFonts.sans,
  },
  assignRowMeta: {
    fontSize: 12,
    color: ticketColors.ink3,
    fontFamily: ticketFonts.sans,
    marginTop: 2,
  },
  assignRowAction: {
    flexShrink: 0,
  },
  typeRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
  },
  typeBtn: {
    paddingVertical: 6,
    paddingHorizontal: 10,
    borderRadius: 8,
    backgroundColor: ticketColors.tile,
    borderWidth: 1,
    borderColor: ticketColors.ruleSoft,
  },
  typeBtnActive: {
    backgroundColor: ticketColors.brandSoft,
    borderColor: ticketColors.brandBorderSoft,
  },
  typeBtnText: {
    fontSize: 12,
    fontWeight: '500',
    color: ticketColors.ink2,
    fontFamily: ticketFonts.sans,
  },
  typeBtnTextActive: {
    color: ticketColors.brandInk,
    fontWeight: '700',
  },
});
